import { useMemo, useState, type ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  BottomNav,
  Button,
  NavCustomizeSheet,
  useNavCustomize,
  type BottomNavItem,
} from "@a2z/ui";
import { useI18n, useTranslate } from "@/lib/i18n";
import { catalogs } from "@/lib/i18n/messages";
import { setPosTheme, usePosThemeChoice } from "@/shared/posTheme";
import { ManageNavList, orderByGroup } from "./ManageNavList";
import { PaperRowIcon } from "./paperNav";
import { matchesExactly, type ManageNavItem } from "./nav";

/** How many destinations the rail shows before the rest fall to the menu. */
const RAIL_SLOTS = 3;

/**
 * Where a store's arrangement is kept. Per STORE, as the seller's is, so an
 * owner of two restaurants keeps two; versioned so a later change of shape can
 * start clean rather than misread an old value.
 */
export function navCustomizeStorageKey(storeSlug: string): string | null {
  return storeSlug ? `restaurant:nav-customize:v1:${storeSlug}` : null;
}

/**
 * The back office's mobile navigation — the seller portal's floating bar.
 *
 * The founder's ask (2026-10-03): "the floating navbar is supposed to behave
 * like the one in Trioty e-commerce". So it is that bar, composed from the
 * shared `BottomNav` (`@a2z/ui`), which now carries the seller's geometry and
 * motion: a pill of three destinations and More, with More growing the pill
 * upward into the whole menu. The seller's Search button is NOT carried over —
 * the founder ruled it out for the restaurant (2026-10-03) — so the pill stands
 * alone, centred.
 *
 * What is this app's:
 *
 * - **The rail.** The first `RAIL_SLOTS` of the permission-filtered list in
 *   GROUP order — service first, because that is what the floor uses — unless
 *   the person rearranged it with Customize (persisted per store). Today (D111)
 *   never takes a slot: it is where bare `/manage` lands, and given one it
 *   pushed the cash drawer out of the rail. It stays in the menu, and out of
 *   Customize, which only arranges what can reach the rail.
 * - **Where you are.** A rail icon lights for its own page; when the page you
 *   are on is not in the rail, More lights instead — the seller's rule, so the
 *   bar always answers "where am I".
 * - **The menu.** Identity (and Customize) first, then the store switcher, then
 *   the GROUPED list — group headings are how this app's nav reads, and a
 *   destination you hid leaves it — then language and sign out.
 */
// COMPOSES the shared `BottomNav` rather than re-implementing a rail: the
// geometry, floating pill, expand behaviour and active treatment
// all come from @a2z/ui, and only the destinations and the menu body are this
// app's.
// eslint-disable-next-line no-restricted-syntax -- composes @a2z/ui's BottomNav
export function ManageBottomNav({
  items,
  storeName,
  storeSlug,
  availableStores,
  onSwitchStore,
  isSwitchingStore,
  userName,
  onLogout,
  isLoggingOut,
}: {
  items: ManageNavItem[];
  storeName: string;
  storeSlug: string;
  availableStores: { storeId: string; storeSlug: string; storeName: string }[];
  onSwitchStore: (slug: string) => void;
  isSwitchingStore: boolean;
  userName?: string;
  onLogout: () => void;
  isLoggingOut: boolean;
}) {
  const t = useTranslate();
  const theme = usePosThemeChoice();
  const { locale } = useI18n();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const [customizeOpen, setCustomizeOpen] = useState(false);

  const iconFor = (item: ManageNavItem): ReactNode => {
    return <PaperRowIcon rowKey={item.rowKey} />;
  };
  /* The same rule the sidebar uses, from the same helper: a row with children
     in the nav lights only on its own URL. Reads the FULL item list. */
  const isActive = (item: ManageNavItem) =>
    pathname === item.to ||
    (!matchesExactly(item.to, items) && pathname.startsWith(`${item.to}/`));

  const ordered = useMemo(() => orderByGroup(items), [items]);
  const today = ordered.find((item) => item.rowKey === "today");
  const arrangeable = useMemo(
    () =>
      ordered
        .filter((item) => item.rowKey !== "today")
        .map((item) => ({ ...item, key: item.rowKey as string })),
    [ordered],
  );
  const customize = useNavCustomize(
    navCustomizeStorageKey(storeSlug),
    arrangeable,
  );

  const rail: BottomNavItem[] = customize.visible
    .slice(0, RAIL_SLOTS)
    .map((item) => ({
      key: item.key,
      label: item.label,
      href: item.to,
      icon: iconFor(item),
      isActive: isActive(item),
    }));
  // The menu keeps its groups; within a group, rows follow the arrangement.
  const menuItems: ManageNavItem[] = [
    ...(today ? [today] : []),
    ...customize.visible,
  ];

  const otherLocale = locale === "ar" ? "en" : "ar";
  const otherLocaleName =
    otherLocale === "ar"
      ? catalogs.ar.shell.languageArabic
      : catalogs.en.shell.languageEnglish;

  const menuActionBase =
    "flex w-full items-center gap-3 rounded-[14px] px-3 py-2.5 text-start text-[13.5px] font-medium transition-colors hover:bg-[color:var(--soft)] disabled:opacity-60";
  const menuActionClass = `${menuActionBase} text-[color:var(--text-primary)]`;

  return (
    <>
      <BottomNav
        label={t((m) => m.nav.label)}
        items={rail}
        renderLink={(item, className, children, style) => (
          <Link
            key={item.key}
            to={item.href ?? "#"}
            aria-label={item.label}
            aria-current={item.isActive ? "page" : undefined}
            data-ui="bottom-nav-item"
            className={className}
            style={style}
          >
            {children}
          </Link>
        )}
        more={{ label: t((m) => m.nav.more) }}
        menu={(close) => (
          <>
            {/* Identity first, as on the sidebar and in the seller's menu — a
                short filtered list explains itself when you can see WHOSE list
                it is — with Customize at its end. */}
            <div
              data-ui="bottom-nav-identity"
              className="flex shrink-0 items-center gap-2.5 px-3 pb-1.5 pt-3"
            >
              <span
                aria-hidden="true"
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] text-[13px] font-bold"
                style={{
                  background: "var(--accent-500)",
                  color: "var(--on-accent)",
                }}
              >
                {Array.from(storeName.trim())[0] ?? ""}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-[13.5px] font-semibold text-[color:var(--text-primary)]">
                  {storeName}
                </p>
                {userName ? (
                  <p className="truncate text-[11.5px] text-[color:var(--text-muted)]">
                    {userName}
                  </p>
                ) : null}
              </div>
              {arrangeable.length > 0 ? (
                <button
                  type="button"
                  onClick={() => {
                    close();
                    setCustomizeOpen(true);
                  }}
                  aria-label={t((m) => m.nav.customize)}
                  data-testid="manage-nav-customize"
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[9px] text-[color:var(--text-muted)] transition-colors hover:bg-[color:var(--soft)] hover:text-[color:var(--text-primary)]"
                >
                  <SlidersGlyph />
                </button>
              ) : null}
            </div>

            {availableStores.length > 1 ? (
              <div
                role="group"
                aria-label={t((m) => m.nav.switchStore)}
                className="flex shrink-0 flex-col gap-1 px-3 py-2"
              >
                <p className="text-xs font-medium text-[color:var(--text-muted)]">
                  {t((m) => m.nav.switchStore)}
                </p>
                {availableStores.map((store) => (
                  <Button
                    key={store.storeId}
                    variant="ghost"
                    size="touch"
                    fullWidth
                    disabled={isSwitchingStore || store.storeSlug === storeSlug}
                    aria-current={
                      store.storeSlug === storeSlug ? "true" : undefined
                    }
                    onClick={() => {
                      close();
                      onSwitchStore(store.storeSlug);
                    }}
                  >
                    {store.storeName}
                  </Button>
                ))}
              </div>
            ) : null}

            <div className="mx-3 my-1 h-px shrink-0 bg-[color:var(--hair)]" />

            <div className="flex flex-col gap-0.5 px-2 pb-1">
              <ManageNavList
                items={menuItems}
                onNavigate={close}
                iconFor={iconFor}
              />
            </div>

            {/* Route-less actions, pinned at the bottom in the destinations'
                row rhythm: the other language, named in that language, and
                sign out. */}
            <div className="mx-3 h-px shrink-0 bg-[color:var(--hair)]" />
            <div className="flex shrink-0 flex-col gap-0.5 p-2">
              <button
                type="button"
                lang={otherLocale}
                onClick={() => {
                  close();
                  navigate(
                    `${pathname.replace(/^\/[^/]+/, `/${otherLocale}`)}${search}`,
                    { replace: true },
                  );
                }}
                data-testid="manage-nav-language"
                className={menuActionClass}
              >
                <span aria-hidden="true" className="flex shrink-0">
                  <GlobeGlyph />
                </span>
                <span className="truncate">{otherLocaleName}</span>
              </button>
              {/* Light or dark, named by what it switches TO, beside the language. */}
              <button
                type="button"
                onClick={() => setPosTheme(theme === "dark" ? "light" : "dark")}
                aria-pressed={theme === "dark"}
                data-testid="manage-nav-theme"
                className={menuActionClass}
              >
                <span aria-hidden="true" className="flex shrink-0">
                  {theme === "dark" ? <SunGlyph /> : <MoonGlyph />}
                </span>
                <span className="truncate">
                  {theme === "dark" ? t((m) => m.nav.themeLight) : t((m) => m.nav.themeDark)}
                </span>
              </button>
              <button
                type="button"
                disabled={isLoggingOut}
                onClick={() => {
                  close();
                  onLogout();
                }}
                data-testid="manage-logout"
                className={`${menuActionBase} text-[color:var(--error-500)]`}
              >
                <span aria-hidden="true" className="flex shrink-0">
                  <LogoutGlyph />
                </span>
                <span className="truncate">{t((m) => m.common.logout)}</span>
              </button>
            </div>
          </>
        )}
      />

      <NavCustomizeSheet
        isOpen={customizeOpen}
        onClose={() => setCustomizeOpen(false)}
        items={customize.ordered.map((item) => ({
          key: item.key,
          label: item.label,
          href: item.to,
          icon: iconFor(item),
          isActive: isActive(item),
        }))}
        hiddenKeys={customize.hiddenKeys}
        onReorder={customize.reorder}
        onHide={customize.hide}
        onShow={customize.show}
        labels={{
          title: t((m) => m.nav.customize),
          close: t((m) => m.common.close),
          show: t((m) => m.nav.customizeShow),
          hide: t((m) => m.nav.customizeHide),
          moveUp: t((m) => m.nav.moveUp),
          moveDown: t((m) => m.nav.moveDown),
        }}
        renderLink={(item, className, children, closeSheet) => (
          <Link
            to={item.href ?? "#"}
            onClick={closeSheet}
            aria-current={item.isActive ? "page" : undefined}
            className={className}
          >
            {children}
          </Link>
        )}
      />
    </>
  );
}

const GLYPH = {
  width: 20,
  height: 20,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.8,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
};

/** Customize (sliders) — the seller's glyph. */
function SlidersGlyph() {
  return (
    <svg {...GLYPH}>
      <path d="M4 6h10" />
      <path d="M18 6h2" />
      <circle cx="16" cy="6" r="2" />
      <path d="M4 12h2" />
      <path d="M10 12h10" />
      <circle cx="8" cy="12" r="2" />
      <path d="M4 18h10" />
      <path d="M18 18h2" />
      <circle cx="16" cy="18" r="2" />
    </svg>
  );
}

function GlobeGlyph() {
  return (
    <svg {...GLYPH}>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18" />
      <path d="M12 3a14 14 0 0 1 0 18 14 14 0 0 1 0-18Z" />
    </svg>
  );
}

function SunGlyph() {
  return (
    <svg {...GLYPH}>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </svg>
  );
}

function MoonGlyph() {
  return (
    <svg {...GLYPH}>
      <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
    </svg>
  );
}

function LogoutGlyph() {
  return (
    <svg {...GLYPH}>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <path d="m16 17 5-5-5-5" />
      <path d="M21 12H9" />
    </svg>
  );
}
